<script>
  import { onMount, onDestroy } from 'svelte'
  import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view'
  import { EditorState } from '@codemirror/state'
  import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
  import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
  import { syntaxHighlighting, defaultHighlightStyle, HighlightStyle } from '@codemirror/language'
  import { tags } from '@lezer/highlight'
  import { hideMarkdown, hideMarkdownTheme } from '../lib/cm-hide-markdown.js'

  let { title, body = null, onSave, onSaved = () => {}, testId = 'neuron-editor' } = $props()
  let container = $state(null)
  let view = null
  let debounceTimer = null
  let lastSavedTitle = title
  let lastSavedBody = body || ''

  function combined(t, b) {
    return b ? `${t}\n\n${b}` : t
  }

  function splitTitleBody(text) {
    const firstNewline = text.indexOf('\n')
    if (firstNewline === -1) return { title: text.trim(), body: null }
    const t = text.slice(0, firstNewline).trim()
    const b = text.slice(firstNewline).replace(/^\n+/, '')
    return { title: t, body: b || null }
  }

  function scheduleAutoSave() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => autoSave(), 1500)
  }

  async function autoSave() {
    if (!view) return
    const text = view.state.doc.toString()
    const { title: newTitle, body: newBody } = splitTitleBody(text)
    if (!newTitle) return
    if (newTitle === lastSavedTitle && (newBody || '') === lastSavedBody) return

    lastSavedTitle = newTitle
    lastSavedBody = newBody || ''
    await onSave(newTitle, newBody)
    onSaved()
  }

  const monoFont = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace'
  const codeHighlight = HighlightStyle.define([
    { tag: tags.monospace, fontFamily: monoFont, fontSize: '0.9em' },
  ])

  const loopsTheme = EditorView.theme({
    '&': {
      backgroundColor: 'transparent',
      color: 'var(--color-text)',
      fontFamily: 'var(--font-body, system-ui, sans-serif)',
      fontSize: '16px',
      lineHeight: '1.6',
    },
    '&.cm-focused': {
      outline: 'none',
    },
    '.cm-content': {
      fontFamily: 'var(--font-body, system-ui, sans-serif)',
      padding: '0',
      caretColor: 'var(--color-text)',
    },
    '.cm-line:first-child': {
      fontFamily: 'var(--font-display, serif)',
      fontSize: '1.5rem',
      fontWeight: '600',
      lineHeight: '1.3',
    },
    '.cm-scroller': {
      overflow: 'auto',
    },
    '&.cm-focused .cm-cursor': {
      borderLeftColor: 'var(--color-text)',
    },
    '.cm-gutters': {
      display: 'none',
    },
    '.cm-md-hidden': {
      fontSize: '0',
      display: 'inline',
      width: '0',
      overflow: 'hidden',
    },
  })

  onMount(() => {
    if (!container) return

    const doc = combined(title, body)

    const state = EditorState.create({
      doc,
      extensions: [
        history(),
        keymap.of([
          { key: 'Mod-Enter', run: (v) => { v.contentDOM.blur(); return true } },
          ...defaultKeymap,
          ...historyKeymap,
        ]),
        markdown({ base: markdownLanguage }),
        syntaxHighlighting(defaultHighlightStyle),
        syntaxHighlighting(codeHighlight),
        hideMarkdown,
        hideMarkdownTheme,
        loopsTheme,
        cmPlaceholder('Add notes...'),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            scheduleAutoSave()
          }
        }),
        EditorView.lineWrapping,
      ],
    })

    view = new EditorView({ state, parent: container })
    container._cmView = view
  })

  onDestroy(() => {
    clearTimeout(debounceTimer)
    if (view) {
      autoSave()
      view.destroy()
      view = null
    }
  })

  $effect(() => {
    if (!view) return
    const currentDoc = view.state.doc.toString()
    const newDoc = combined(title, body)
    if (currentDoc !== newDoc && newDoc !== combined(lastSavedTitle, lastSavedBody)) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: newDoc },
      })
      lastSavedTitle = title
      lastSavedBody = body || ''
    }
  })
</script>

<div
  bind:this={container}
  data-testid={testId}
  class="neuron-editor"
></div>

<style>
  .neuron-editor {
    min-height: 4rem;
  }
  .neuron-editor :global(.cm-editor) {
    min-height: 4rem;
  }
</style>
